import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Internationaler Handel";
const heroImage = "https://net-basis.net/Bilder/international-trade-dach-local-expertise.webp";
const heroAlt = "Geschäftsmann zeigt auf eine vernetzte Weltkarte für internationalen Handel in der DACH-Region.";
const heroClaimLine1 = "Grenzen überwinden!";
const heroClaimLine2 = "Mit lokalem Know-how!";
const benefitsTitle = "Wie Sie von unserer Expertise im Bereich __Internationaler Handel__ profitieren:";
const h2Intro = "NetBasis International ist für seine Kunden der verbundene und vertrauenswürdige Handelspartner in unseren Märkten. Wir begleiten unsere ausländischen Herstellerpartner dabei, mit Unternehmen vor Ort und lokalen Partnern oder Kunden neue Geschäftsaktivitäten zu starten.";
const p1Text = "Wir führen notwendige und relevante Markt- und Handelsanalysen durch und sorgen so für das wettbewerbsrelevante Wissen. NetBasis International unterstützt seine Herstellerpartner bei der Einschätzung ihrer Geschäfts- und Kundenpotenziale, der Evaluierung möglicher Partnerschaften sowie bei Fragen zu Qualitätskontrolle, Management, Führungsstil und Finanzen.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Findung der effektivsten Preisstruktur sowie optimaler Zahlungs- und Garantiebedingungen durch Unterstützung bei Vertragsverhandlungen",
  "Juristische Freigabe und sicherer Abschluss neuer Geschäftsvorhaben in Zusammenarbeit mit dem Management, Rechtsabteilungen oder lokalen Anwaltskanzleien",
  "Wertvolle Einblicke und kontinuierliche Updates zu lokalen Handelsmethoden und gesetzlichen Handelsbestimmungen",
  "Umfassende Unterstützung bei der Erlangung notwendiger Handelslizenzen und strategischer Handelsabkommen",
  "Fachliche Beantwortung und Klärung aller Fragen im Zusammenhang mit internationalen Handelsverfahren",
  "Bearbeitung von Anträgen zu Einfuhrverfahren sowie von Zollerklärungen und zugehöriger Dokumentation",
  "Bereitstellung verlässlicher Lieferzeiten und stabiler Lieferpläne durch gezielte Unterstützung der Lieferkette",
  "Gewährleistung der vollständigen Sende- und Versandkonformität bezüglich nationaler und internationaler Regeln und Vorschriften",
];

export default function InternationalerHandelDePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Internationaler Handel | NetBasis International"
        description="Ihr kompetentes Bindeglied für globale Handelsstrukturen und erfolgreiche B2B-Geschäftsbeziehungen im ITK-Sektor."
        ogUrl="https://net-basis.net/de/services/business-development/internationaler-handel"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-center pt-[28%] md:pt-[11%] lg:pt-[14%] pointer-events-none">
                <p
                  className="font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[globusTextSlide_12s_ease-in-out_infinite] max-w-[85%] sm:max-w-[80%] text-[1.65rem] sm:text-3xl md:text-4xl lg:text-5xl"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  <span className="block">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {h2Intro}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}